/** Live game connection + authoritative snapshot, driven by the WebSocket. */
import { create } from "zustand";
import {
  index as cellIndex,
  parseServerMessage,
  type ClientMessage,
  type GameSnapshot,
  type Team,
} from "@msf/shared";
import { api, ApiError } from "../lib/api.js";

type ConnState = "idle" | "connecting" | "open" | "closed" | "error";

interface RevealEvent {
  seq: number;
  cells: number[];
  by: Team;
  mineHit: boolean;
  /** The tapped cell (move) or bomb centre — where the last move happened,
   * as opposed to `cells`, which is everything it revealed via flood-fill. */
  origin: number;
}

type GameOverReason = "normal" | "forfeit";

interface GameStore {
  conn: ConnState;
  snapshot: GameSnapshot | null;
  /** The game id doesn't resolve to anything (never existed, or existed and
   * was since cleaned up after finishing/going idle) — a dead link, not a
   * connectivity problem. Distinct from `conn === "error"`, which covers
   * transient network trouble that's worth silently retrying. */
  notFound: boolean;
  lastReveal: RevealEvent | null;
  /** Index of a cell we just tapped, optimistically, before the server has
   * confirmed the move. We never know its true content ahead of time (mine
   * layout is server-only, by design) — this only drives the immediate
   * "cracking" transitional visual + input lock, cleared on the resolving
   * `reveal`/`error`. */
  pendingCell: number | null;
  gameOverReason: GameOverReason | null;
  rematchPendingBy: Team | null;
  toast: string | null;
  connect: (gameId: string, token: string) => void;
  disconnect: () => void;
  move: (x: number, y: number) => void;
  bomb: (x: number, y: number) => void;
  sendChat: (text: string) => void;
  rematch: () => void;
  resign: () => void;
  clearToast: () => void;
}

// Kept outside the store so socket churn doesn't trigger re-renders.
let socket: WebSocket | null = null;
let currentGameId: string | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let revealSeq = 0;
let attempts = 0;
let manualClose = false;

export const useGameStore = create<GameStore>((set, get) => {
  function send(msg: ClientMessage) {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
  }

  function open(gameId: string, token: string) {
    manualClose = false;
    currentGameId = gameId;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/games/${encodeURIComponent(gameId)}/ws?token=${encodeURIComponent(token)}`;
    set({ conn: "connecting" });
    const ws = new WebSocket(url);
    socket = ws;

    ws.onopen = () => {
      attempts = 0;
      set({ conn: "open" });
      send({ type: "hello" });
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = setInterval(() => send({ type: "ping" }), 25_000);
    };

    ws.onmessage = (ev) => {
      let raw: unknown;
      try {
        raw = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      const msg = parseServerMessage(raw);
      if (!msg) return;
      const snap = get().snapshot;
      switch (msg.type) {
        case "snapshot":
          set({
            snapshot: msg.game,
            pendingCell: null,
            // A fresh active snapshot means either a first hello or a rematch
            // restart — either way there's no "last move" from this session
            // yet, so drop any stale highlight from a previous game.
            ...(msg.game.status === "active"
              ? { rematchPendingBy: null, gameOverReason: null, lastReveal: null }
              : {}),
          });
          break;
        case "reveal": {
          if (!snap) break;
          const cells = snap.cells.slice();
          for (const { i, cell } of msg.cells) cells[i] = cell;
          set({
            snapshot: {
              ...snap,
              cells,
              scores: msg.scores,
              turn: msg.turn,
              minesRemaining: msg.minesRemaining,
              bombsAvailable: msg.bombsAvailable,
            },
            lastReveal: {
              seq: ++revealSeq,
              cells: msg.cells.map((c) => c.i),
              by: msg.by,
              mineHit: msg.mineHit,
              origin: msg.origin,
            },
            // Turn order guarantees the next reveal while we have a pending
            // tap is the resolution of *our* tap — the opponent can't move
            // until the turn passes to them.
            pendingCell: null,
          });
          break;
        }
        case "chat":
          if (!snap) break;
          set({ snapshot: { ...snap, chat: [...snap.chat, msg.message].slice(-200) } });
          break;
        case "presence":
          if (!snap) break;
          set({ snapshot: { ...snap, players: msg.players, status: msg.status } });
          break;
        case "gameOver":
          if (!snap) break;
          set({
            snapshot: { ...snap, status: "finished", winner: msg.winner, scores: msg.scores },
            gameOverReason: msg.reason ?? "normal",
          });
          break;
        case "rematchPending":
          set({ rematchPendingBy: msg.by });
          break;
        case "error":
          // Roll back the optimistic tap — the tile snaps back to its normal
          // hidden state, no shard overlay left behind.
          set({ toast: humanError(msg.code), pendingCell: null });
          break;
        case "pong":
          break;
      }
    };

    ws.onclose = () => {
      set({ conn: manualClose ? "closed" : "connecting" });
      if (pingTimer) clearInterval(pingTimer);
      if (!manualClose && currentGameId) {
        attempts++;
        // The WebSocket API gives no reason for a failed upgrade, so a dead
        // link (finished + cleaned up, or never existed) looks identical to a
        // network hiccup and would otherwise retry forever. After a few
        // failures with no snapshot ever received, confirm via REST — a
        // reconnect that's already showing a live game is left alone.
        if (attempts >= 3 && !get().snapshot) {
          api.getGame(gameId, token).catch((e: unknown) => {
            if (e instanceof ApiError && e.status === 404 && currentGameId === gameId) {
              manualClose = true;
              if (reconnectTimer) clearTimeout(reconnectTimer);
              set({ notFound: true, conn: "error" });
            }
          });
        }
        const delay = Math.min(1000 * 2 ** attempts, 15_000);
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => open(gameId, token), delay);
      }
    };

    ws.onerror = () => set({ conn: "error" });
  }

  return {
    conn: "idle",
    snapshot: null,
    notFound: false,
    lastReveal: null,
    pendingCell: null,
    gameOverReason: null,
    rematchPendingBy: null,
    toast: null,
    async connect(gameId, token) {
      if (socket && currentGameId === gameId && socket.readyState <= WebSocket.OPEN) return;
      get().disconnect();
      currentGameId = gameId;
      manualClose = false;
      set({ conn: "connecting" });
      // Check the game actually exists before ever opening a socket — a dead
      // link should say so immediately, not sit on "Connecting…" forever.
      try {
        await api.getGame(gameId, token);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          if (currentGameId === gameId) set({ notFound: true, conn: "error" });
          return;
        }
        // Any other failure (offline, unauthorized, ...) — fall through and
        // let the WebSocket's own connect/retry loop take it from here.
      }
      if (currentGameId !== gameId) return; // superseded by a newer connect()
      open(gameId, token);
    },
    disconnect() {
      manualClose = true;
      currentGameId = null;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (socket) {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
        socket = null;
      }
      set({
        conn: "idle",
        snapshot: null,
        notFound: false,
        lastReveal: null,
        pendingCell: null,
        gameOverReason: null,
        rematchPendingBy: null,
      });
    },
    move(x, y) {
      // Optimistic: lock the tile + start its "revealing" transition
      // immediately, before the server has confirmed anything.
      set({ pendingCell: cellIndex(x, y) });
      send({ type: "move", x, y });
    },
    bomb(x, y) {
      set({ pendingCell: cellIndex(x, y) });
      send({ type: "bomb", x, y });
    },
    sendChat(text) {
      const trimmed = text.trim();
      if (trimmed) send({ type: "chat", text: trimmed });
    },
    rematch() {
      // Optimistic: show "waiting on opponent" immediately rather than after
      // the round trip; the real rematchPending/snapshot broadcast confirms it.
      const you = get().snapshot?.you;
      set({ gameOverReason: null, ...(you ? { rematchPendingBy: you } : {}) });
      send({ type: "rematch" });
    },
    resign() {
      send({ type: "resign" });
    },
    clearToast() {
      set({ toast: null });
    },
  };
});

function humanError(code: string): string {
  switch (code) {
    case "not_your_turn":
      return "Hold on — it's not your turn!";
    case "already_revealed":
      return "That tile's already open.";
    case "bomb_already_used":
      return "You've already used your bomb.";
    case "game_not_active":
      return "The game isn't running.";
    case "not_a_player":
      return "You're spectating this game.";
    default:
      return "Something went wrong.";
  }
}
